import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import FormTextarea from 'components/Common/FormTextarea';
import BasicModal from 'components/Common/BasicModal';

import { auth, onUpdateDoc, setFirestoreDoc } from 'utils/config/firebase';
import { USER_SIGNATURES } from 'utils/config/constants/controllerNames';
import { Inputs, Props } from './types';

import { Box } from '@mui/material';

import { getTranslatedDataLanguages } from 'helpers/filters';
import { saveMessage } from 'helpers/messages';
import { useAppSelector } from 'app/hooks';
import { sanitizeAndDeleteHtml } from 'helpers/htmlParser';

const SignatureModal: FC<Props> = ({ open, onClose }) => {
    const { t } = useTranslation();
    const { userSignature, outputLanguages } = useAppSelector((state) => state.global);
    const { userSettings } = useAppSelector((state) => state.auth);
    const { register, handleSubmit, control, reset } = useForm<Inputs>({
        values: userSignature as Inputs,
    });
    const languages = outputLanguages?.filter(
        (language) => language.country_code === userSettings?.target_country_code,
    );

    const onSubmit = (values: Inputs) => {
        const data: Record<string, string | undefined> = {
            ...values,
            user_id: auth?.currentUser?.uid,
        };

        // sanitize to prevent XSS
        for (const [key, value] of Object.entries(values)) {
            data[key] = sanitizeAndDeleteHtml(value);
        }

        if (userSignature?.documentId) {
            onUpdateDoc(USER_SIGNATURES, userSignature.documentId, data);
        } else {
            setFirestoreDoc(USER_SIGNATURES, auth?.currentUser?.uid as string, data);
        }

        reset(data);
        saveMessage();
        onClose();
    };

    return (
        <BasicModal open={open} onClose={onClose}>
            <Box className="signature-modal">
                <Box className="signature-modal-title">{t('web_app.settings.signature.title')}</Box>
                <Box className="signature-modal-content">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Box className="signature-modal-content-boxes">
                            {getTranslatedDataLanguages(languages)?.map((item) => (
                                <Box key={item.document_id}>
                                    <FormTextarea
                                        name={`${item.country_code}-${item.language_code}`}
                                        title={item.language_name}
                                        register={register}
                                        control={control}
                                    />
                                </Box>
                            ))}
                        </Box>
                        <Box className="signature-modal-content-btns">
                            <button type="button" onClick={onClose}>
                                {t('web_app.settings.signature.action.cancel')}
                            </button>
                            <button>{t('web_app.settings.signature.action.ok')}</button>
                        </Box>
                    </form>
                </Box>
            </Box>
        </BasicModal>
    );
};

export default SignatureModal;
